import { useEffect, useRef, useState } from 'react';

const WS_URL = 'wss://dapp-iro.ironetwork.com/ws';
const HISTORY_BARS = 24;
const HEARTBEAT_MS = 12000;
const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 20000;

function deriveFromBars(bars) {
  if (!bars || bars.length === 0) {
    return { priceUsd: null, priceChange24h: null };
  }
  const recent = bars.slice(-HISTORY_BARS);
  const first = parseFloat(recent[0].close);
  const last = parseFloat(recent[recent.length - 1].close);
  const priceChange24h =
    Number.isFinite(first) && first > 0 && Number.isFinite(last)
      ? ((last - first) / first) * 100
      : null;
  return {
    priceUsd: Number.isFinite(last) ? last : null,
    priceChange24h,
  };
}

export function usePriceFeed({ projectId, timeframe = 'hour' }) {
  const [state, setState] = useState({
    bars: [],
    priceUsd: null,
    priceChange24h: null,
    loading: true,
    connected: false,
  });

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const reqMsg = `${timeframe}_${projectId}`;
    const subMsg = `newPrice_${timeframe}_${projectId}`;

    const applyHistory = (data) => {
      const sorted = [...data].sort((a, b) => Number(a.time) - Number(b.time));
      const trimmed = sorted.slice(-HISTORY_BARS);
      const derived = deriveFromBars(trimmed);
      setState((s) => ({ ...s, bars: trimmed, ...derived, loading: false }));
    };

    const applyPush = (bar) => {
      setState((s) => {
        const t = Number(bar.time);
        let bars;
        if (s.bars.length && Number(s.bars[s.bars.length - 1].time) === t) {
          bars = [...s.bars.slice(0, -1), bar];
        } else {
          bars = [...s.bars, bar].slice(-HISTORY_BARS);
        }
        const derived = deriveFromBars(bars);
        return { ...s, bars, ...derived, loading: false };
      });
    };

    const onMessage = (ev) => {
      if (cancelledRef.current) return;
      let obj;
      try { obj = JSON.parse(ev.data); } catch (e) { return; }
      if (!obj || obj.code !== 200) return;
      const sub = obj.sub || '';
      const matchesUs = sub === reqMsg || sub === subMsg || obj.type === timeframe;
      if (!matchesUs) return;
      if (Array.isArray(obj.data)) {
        if (obj.data.length) applyHistory(obj.data);
      } else if (obj.data && typeof obj.data === 'object' && obj.data.time != null) {
        applyPush(obj.data);
      }
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current) return;
      const attempt = ++reconnectAttemptRef.current;
      const delay = Math.min(
        RECONNECT_BASE_MS * 2 ** (attempt - 1),
        RECONNECT_MAX_MS
      );
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    function connect() {
      if (cancelledRef.current) return;
      let ws;
      try {
        ws = new WebSocket(WS_URL);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelledRef.current) {
          try { ws.close(); } catch (e) {}
          return;
        }
        reconnectAttemptRef.current = 0;
        setState((s) => ({ ...s, connected: true }));
        try {
          ws.send(JSON.stringify({ type: 'req', msg: reqMsg }));
          ws.send(JSON.stringify({ type: 'sub', msg: subMsg }));
          ws.send(JSON.stringify({ type: 'ping', msg: '' }));
        } catch (e) {}
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping', msg: '' })); } catch (e) {}
          }
        }, HEARTBEAT_MS);
      };

      ws.onmessage = onMessage;
      ws.onerror = () => {};
      ws.onclose = () => {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
        setState((s) => ({ ...s, connected: false }));
        if (!cancelledRef.current) scheduleReconnect();
      };
    }

    connect();

    return () => {
      cancelledRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(heartbeatRef.current);
      const ws = wsRef.current;
      if (ws) {
        try {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'unsub', msg: subMsg }));
          }
          ws.close();
        } catch (e) {}
      }
    };
  }, [projectId, timeframe]);

  return state;
}
